import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import './Unsubscribe.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, valid, unsubscribed, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid unsubscribe link. No token provided.');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-v2/unsubscribe/verify?token=${encodeURIComponent(token)}`);
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setEmail(data.email || '');
        setStatus('valid');
      } else {
        setStatus('error');
        setError(data.detail || 'Invalid or expired unsubscribe link.');
      }
    } catch (err) {
      setStatus('error');
      setError('Unable to verify unsubscribe link. Please try again later.');
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/email-v2/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus('unsubscribed');
      } else {
        setStatus('error');
        setError(data.detail || 'Failed to unsubscribe. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setError('Unable to process unsubscribe request. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="unsubscribe-page">
      <div className="unsubscribe-container">
        {/* Header */}
        <div className="unsubscribe-header">
          <div className="brand-logo">⛳ Golf Nuts</div>
          <p className="brand-tagline">The Golf World's Group Chat</p>
        </div>

        {/* Content based on status */}
        <div className="unsubscribe-content">
          {status === 'verifying' && (
            <div className="status-card">
              <div className="loading-spinner" />
              <h2>Verifying your link...</h2>
              <p>Please wait while we verify your unsubscribe request.</p>
            </div>
          )}

          {status === 'valid' && (
            <div className="status-card">
              <AlertTriangle size={48} className="icon-warning" />
              <h2>Unsubscribe from emails?</h2>
              {email && <p className="email-display">{email}</p>}
              <p>You will no longer receive promotional emails from Golf Nuts, including:</p>
              <ul className="unsubscribe-list">
                <li>Giveaway announcements</li>
                <li>Contest updates</li>
                <li>New episode alerts</li>
                <li>Special promotions</li>
              </ul>
              <p className="note">
                <strong>Note:</strong> You will still receive transactional emails 
                (password reset, account updates).
              </p>
              <button 
                className="btn-unsubscribe"
                onClick={handleUnsubscribe}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Yes, Unsubscribe Me'}
              </button>
              <button 
                className="btn-cancel"
                onClick={() => navigate('/')}
              >
                Cancel
              </button>
            </div>
          )}

          {status === 'unsubscribed' && (
            <div className="status-card success">
              <CheckCircle size={48} className="icon-success" />
              <h2>You're unsubscribed</h2>
              <p>You will no longer receive promotional emails from Golf Nuts.</p>
              <div className="resubscribe-note">
                <p>Changed your mind? You can resubscribe anytime from your Clubhouse profile.</p>
              </div>
              <div className="action-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => navigate('/clubhouse/settings')}
                >
                  Go to Clubhouse Profile
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => navigate('/')}
                >
                  <ArrowLeft size={16} />
                  Return to Home
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="status-card error">
              <XCircle size={48} className="icon-error" />
              <h2>Something went wrong</h2>
              <p>{error}</p>
              <div className="action-buttons">
                <button 
                  className="btn-secondary"
                  onClick={() => navigate('/')}
                >
                  <ArrowLeft size={16} />
                  Return to Home
                </button>
              </div>
              <p className="support-note">
                Need help? Contact us at{' '}
                <a href="mailto:support@golfnutsofficial.com">support@golfnutsofficial.com</a>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="unsubscribe-footer">
          <p>© 2024 Golf Nuts. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default Unsubscribe;
